import { useState } from "react";
import { ContactRound, Linkedin, Phone, Mail, ArrowRight, Globe } from "lucide-react";
import LivingCanvas from "../../components/marketing/LivingCanvas";
import BrainViz from "../../components/marketing/BrainViz";
import Seo from "../../components/seo/Seo";
import jamesImg from "../../assets/images/james-card.gif";
import logoWhite from "../../assets/brand/logo-white.svg";
import "./JamesCard.css";

const VCARD = `BEGIN:VCARD
VERSION:3.0
N:Card;James;;;
FN:James Card
ORG:Data Chimp Labs
TITLE:Founder & CEO
TEL;TYPE=CELL:+16024225664
EMAIL:james.card@datachimplabs.com
URL:datachimplabs.com
END:VCARD`;

/*
  ALTERNATIVE: Static file "Add to Contacts" (better iOS experience)
  ───────────────────────────────────────────────────────────────────
  Once Amplify Console custom headers are configured, replace the
  downloadVCard <button> below with this plain <a> link:

    <a href="/james-card.vcf" className="button button-primary biz-card__cta-btn">
      Add to Contacts
    </a>

  Steps to enable:
  1. Amplify Console → App settings → Custom headers → paste:
       customHeaders:
         - pattern: "*.vcf"
           headers:
             - key: Content-Type
               value: text/vcard
             - key: Content-Disposition
               value: attachment; filename="james-card.vcf"
  2. Confirm public/james-card.vcf exists in the repo (it does).
  3. Deploy. iOS Safari will then download the file and offer the native
     "Add to Contacts" sheet instead of a forced blob download.
*/
const downloadVCard = () => {
  const blob = new Blob([VCARD], { type: "text/vcard" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "james-card.vcf";
  a.click();
  URL.revokeObjectURL(url);
};

const JamesCard = () => {
  const [copied, setCopied] = useState(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <>
      <Seo
        title="James Card — Founder & CEO | Data Chimp Labs"
        description="James Card, Founder & CEO of Data Chimp Labs. Twenty years in healthcare. Nine-figure exit."
        pathname="/team/james-card/card"
        noIndex
      />

      <LivingCanvas />

      <div className="biz-card-shell">
        <main className="biz-card">
          <div className="biz-card__photo-wrap">
            <img
              src={jamesImg}
              alt="James Card"
              className="biz-card__photo"
              width="120"
              height="120"
            />
          </div>

          <div className="biz-card__body">
            <h1 className="biz-card__name">James Card</h1>
            <a href="https://datachimplabs.com" target="_blank" rel="noopener noreferrer" className="biz-card__title">Founder &amp; CEO <span style={{ color: "var(--color-text-primary)" }}>@ Data Chimp Labs</span></a>
            <p className="biz-card__desc">
              Twenty years in healthcare. Nine-figure exit.
              Building the operating brain for healthcare organizations.
            </p>

            <div className="biz-card__contacts">
              <div className="biz-card__contact-row">
                <a href="tel:+16024225664" className="biz-card__cta-icon">
                  <Phone size={20} />
                </a>
                <a href="tel:+16024225664" className="biz-card__contact-item">
                  <span className="biz-card__contact-label">Phone</span>
                  <span className="biz-card__contact-value">+1 (602) 422-5664</span>
                </a>
                <button
                  className="biz-card__copy-btn"
                  onClick={() => copy("+16024225664", "phone")}
                  type="button"
                >
                  {copied === "phone" ? "Copied!" : "Copy"}
                </button>
              </div>

              <div className="biz-card__contact-row">
                <a href="mailto:james.card@datachimplabs.com" className="biz-card__cta-icon">
                  <Mail size={20} />
                </a>
                <a href="mailto:james.card@datachimplabs.com" className="biz-card__contact-item">
                  <span className="biz-card__contact-label">Email</span>
                  <span className="biz-card__contact-value">james.card@datachimplabs.com</span>
                </a>
                <button
                  className="biz-card__copy-btn"
                  onClick={() => copy("james.card@datachimplabs.com", "email")}
                  type="button"
                >
                  {copied === "email" ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>

            <div className="biz-card__cta-group">
              <div className="biz-card__cta-row">
                <button className="biz-card__cta-icon" onClick={downloadVCard} type="button">
                  <ContactRound size={20} />
                </button>
                <button
                  className="biz-card__cta-btn"
                  onClick={downloadVCard}
                  type="button"
                >
                  <span className="biz-card__contact-label">vCard</span>
                  <span className="biz-card__contact-value">Add to Contacts</span>
                </button>
                <button className="biz-card__cta-arrow" onClick={downloadVCard} type="button">
                  <ArrowRight size={16} />
                </button>
              </div>

              <div className="biz-card__cta-row">
                <a href="https://www.linkedin.com/in/james-card-msds" target="_blank" rel="noopener noreferrer" className="biz-card__cta-icon">
                  <Linkedin size={20} />
                </a>
                <a
                  href="https://www.linkedin.com/in/james-card-msds"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="biz-card__cta-btn"
                >
                  <span className="biz-card__contact-label">Connect</span>
                  <span className="biz-card__contact-value">LinkedIn</span>
                </a>
                <a href="https://www.linkedin.com/in/james-card-msds" target="_blank" rel="noopener noreferrer" className="biz-card__cta-arrow">
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>

            <div className="biz-card__divider" />

            <div className="biz-card__company-block">
              <BrainViz className="biz-card__company-canvas" />

              <a href="https://datachimplabs.com" target="_blank" rel="noopener noreferrer">
                <img
                  src={logoWhite}
                  alt="Data Chimp Labs"
                  className="biz-card__logo"
                  width="160"
                  height="45"
                />
              </a>

              <p className="biz-card__company-desc">
                Building the operating brain for healthcare organizations. AI-powered intelligence that helps operators move faster, cut waste, and deliver better outcomes.
              </p>

            <div className="biz-card__cta-row">
              <a href="https://datachimplabs.com" target="_blank" rel="noopener noreferrer" className="biz-card__cta-icon">
                <Globe size={20} />
              </a>
              <a
                href="https://datachimplabs.com"
                target="_blank"
                rel="noopener noreferrer"
                className="biz-card__cta-btn"
              >
                <span className="biz-card__contact-label">Website</span>
                <span className="biz-card__contact-value">datachimplabs.com</span>
              </a>
              <a href="https://datachimplabs.com" target="_blank" rel="noopener noreferrer" className="biz-card__cta-arrow">
                <ArrowRight size={16} />
              </a>
            </div>
            </div>{/* end biz-card__company-block */}

          </div>
        </main>
      </div>

      <div className="biz-card__sticky-actions">
        <div className="biz-card__sticky-inner">
          <a
            href="mailto:james.card@datachimplabs.com"
            className="button button-secondary biz-card__btn"
          >
            Email Now
          </a>

          <a
            href="tel:+16024225664"
            className="button button-primary biz-card__btn"
          >
            Call Now
          </a>
        </div>
      </div>
    </>
  );
};

export default JamesCard;
